import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaPlus } from "react-icons/fa";
import { BiSolidDownArrow } from "react-icons/bi";
import { FiPrinter } from "react-icons/fi";
import { TbFileDownload } from "react-icons/tb";
import { TiPencil } from "react-icons/ti";
import * as XLSX from 'xlsx';

const LabProcedureReport = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isAddHovered, setIsAddHovered] = useState(false);
  const [isArrowHovered, setIsArrowHovered] = useState(false);
  const navigate = useNavigate();

  /* ------------------- Fetch procedures ------------------- */
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Get userLabId from localStorage
        const userLabId = localStorage.getItem('userLabId');
       
        let apiEndpoint = 'https://hms.backend.hiragroupcloud.com/api/procedures';
        
        // If userLabId exists, use the specific lab endpoint
        // If not, fetch all procedures
        if (userLabId) {
          apiEndpoint = `https://hms.backend.hiragroupcloud.com/api/procedures/lab/${userLabId}`;
        }
       
        // Fetch procedures based on userLabId existence
        const response = await axios.get(apiEndpoint, { withCredentials: true });
       
        // Validate and set data
        const validData = response.data.data || response.data || [];
        
        // Additional filtering for userLabId endpoint
        if (userLabId && Array.isArray(validData)) {
          // Ensure we only get procedures for this lab
          const filteredByLab = validData.filter(
            (item) =>
              item &&
              item.addedBy &&
              String(item.addedBy.userLabId) === userLabId
          );
          setData(filteredByLab);
          setFilteredData(filteredByLab);
        } else {
          // For all procedures endpoint or if no userLabId
          const validatedData = validData.filter(
            (item) =>
              item &&
              typeof item.name === 'string'
          );
          setData(validatedData);
          setFilteredData(validatedData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        
        // Fallback: try the general endpoint if lab-specific fails
        try {
          const fallbackResponse = await axios.get('https://hms.backend.hiragroupcloud.com/api/procedures', { withCredentials: true });
          const fallbackData = fallbackResponse.data.data || fallbackResponse.data || [];
          
          const userLabId = localStorage.getItem('userLabId');
          if (userLabId) {
            // Filter by userLabId manually
            const filteredByLab = fallbackData.filter(
              (item) =>
                item &&
                item.addedBy &&
                String(item.addedBy.userLabId) === userLabId
            );
            setData(filteredByLab);
            setFilteredData(filteredByLab);
          } else {
            setData(fallbackData);
            setFilteredData(fallbackData);
          }
        } catch (fallbackError) {
          console.error('Fallback fetch also failed:', fallbackError);
          setData([]);
          setFilteredData([]);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  /* ------------------- Filter data based on search term ------------------- */
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(data);
    } else {
      const filtered = data.filter(
        (item) =>
          (typeof item.name === 'string' && item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (typeof item.category === 'string' && item.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (typeof item.price === 'string' && item.price.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (typeof item.quickCode === 'string' && item.quickCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (typeof item.procedureId === 'string' && item.procedureId.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.procedureId && item.procedureId.toString().toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  /* ------------------- Edit – store MongoDB _id in sessionStorage ------------------- */
  const handleEdit = (item) => {
    const userLabId = localStorage.getItem('userLabId');
    if (userLabId && String(item.addedBy?.userLabId) !== userLabId) {
      alert('You do not have permission to edit this procedure.');
      return;
    }
    sessionStorage.setItem('procedureId', item._id);
    navigate('/Addlabprocedure', { state: { item } });
  };

  /* ------------------- Add – clear any previous ID ------------------- */
  const handleAddClick = () => {
    sessionStorage.removeItem('procedureId');
    navigate('/Addlabprocedure');
  };

  /* ------------------- Delete ------------------- */
  const confirmDelete = (id) => {
    setSelectedItemId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`https://hms.backend.hiragroupcloud.com/api/procedures/${selectedItemId}`, { withCredentials: true });
      // Update both data and filteredData
      setData(prev => prev.filter(item => item._id !== selectedItemId));
      setFilteredData(prev => prev.filter(item => item._id !== selectedItemId));
      setShowDeleteModal(false);
      alert('Procedure deleted successfully!');
    } catch (error) {
      console.error('Error deleting procedure:', error);
      alert('Failed to delete procedure. Please try again.');
    }
  };

  /* ------------------- Dropdown handlers ------------------- */
  const toggleDropdown = () => setShowDropdown(prev => !prev);
  const handleMultiEdit = () => { navigate('/multi-edit'); setShowDropdown(false); };
  const handleAssignProcedureGroups = () => { navigate('/procedure-groups'); setShowDropdown(false); };
  const handlePriceListsGroups = () => { navigate('/price-list-table'); setShowDropdown(false); };
  const handleProcedurePricesEdit = () => { navigate('/edit-price-lists'); setShowDropdown(false); };
  const handleAttributePricesEdit = () => { navigate('/edit-attribute-price'); setShowDropdown(false); };
  const handleProcedureQuickCodes = () => { navigate('/procedure-quick-codes'); setShowDropdown(false); };
  
  const handleExportToExcel = () => {
    if (filteredData.length === 0) {
      alert('No data to export');
      return;
    }
    const exportData = filteredData.map(item => {
      const defaultPriceObj = item.prices?.find(price => price.target === "default") || item.prices?.[0];
      const defaultPrice = defaultPriceObj?.price || item.price || '0';
      return {
        'Procedure ID': item.procedureId || '',
        'Name': item.name || '',
        'Description': item.script || '',
        'Quick Code': item.quickCode || '',
        'Category': item.category || '',
        'Sub Category': item.subCategory || '',
        'Type': item.type || '',
        'Sort Rank': item.sortRank || '',
        'Count By': item.unitCount || '',
        'Sales Description': item.salesDescription || '',
        'Turn Around': item.turnAroundDays || '',
        'Default Price': defaultPrice,
        'Added By': item.addedBy?.userLabId || '',
        'Added At': item.addedBy?.addedAt ? new Date(item.addedBy.addedAt).toLocaleDateString() : ''
      };
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lab Procedures');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lab_procedures.xlsx';
    a.click();
    URL.revokeObjectURL(url);
    setShowDropdown(false);
  };

  /* ------------------- Click-outside dropdown ------------------- */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.dropdown-container')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  if (isLoading) return <div>Loading...</div>;

  /* ------------------- Styles ------------------- */
  const addBaseStyle = {
    backgroundColor: '#004aad',
    color: 'white',
    padding: '8px 15px',
    borderColor: '#004aad',
    borderStyle: 'solid',
    borderWidth: '2px 1px 2px 2px',
    borderTopLeftRadius: '5px',
    borderBottomLeftRadius: '5px',
    borderTopRightRadius: '0px',
    borderBottomRightRadius: '0px',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
  };
  
  const addHoverStyle = { backgroundColor: '#003080', color: '#f0f0f0' };
  
  const arrowBaseStyle = {
    backgroundColor: '#004aad',
    color: 'white',
    padding: '8px 10px',
    borderColor: '#004aad',
    borderStyle: 'solid',
    borderWidth: '2px 2px 2px 1px',
    borderTopLeftRadius: '0px',
    borderBottomLeftRadius: '0px',
    borderTopRightRadius: '5px',
    borderBottomRightRadius: '5px',
    display: 'inline-flex',
    alignItems: 'center',
  };
  
  const arrowHoverStyle = { backgroundColor: '#003080', color: '#f0f0f0' };
  
  const arrowIconBaseStyle = { fontSize: '12px' };
  const arrowIconHoverStyle = { color: '#ffffff' };

  return (
    <div >
      <h1 className="title" >
        Lab Procedures
      </h1>
      <div style={{ width: '1560px',backgroundColor:"#f4f4f4",padding:"10px",margin:"0 auto" }}>
      {/* Search + Add */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', width: '100%' }}>
        <div className="search" style={{ flexGrow: 1 }}>
          <div className="row align-items-center">
            <div className="col-md-6 d-flex justify-content-start">
              <div className="col-md-3">
                <input
                  type="text"
                  id="searchInput"
                  className="form-control"
                  placeholder="Search by name, category, price, quick code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '5px' }}
                />
              </div>
            </div>
            <div className="col-md-6 d-flex justify-content-end">
              <div className="dropdown-container" style={{ position: 'relative', display: 'inline-block' }}>
                <div className="btn-group">
                  {/* Add button – clears sessionStorage */}
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{
                      ...addBaseStyle,
                      ...(isAddHovered ? addHoverStyle : {}),
                    }}
                    onMouseEnter={() => setIsAddHovered(true)}
                    onMouseLeave={() => setIsAddHovered(false)}
                    onClick={handleAddClick}
                  >
                    <FaPlus />Add
                  </button>
                  {/* Dropdown arrow */}
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{
                      ...arrowBaseStyle,
                      ...(isArrowHovered ? arrowHoverStyle : {}),
                    }}
                    onClick={toggleDropdown}
                    onMouseEnter={() => setIsArrowHovered(true)}
                    onMouseLeave={() => setIsArrowHovered(false)}
                  >
                    <span
                      style={{
                        ...arrowIconBaseStyle,
                        ...(isArrowHovered ? arrowIconHoverStyle : {}),
                      }}
                    >
                      <BiSolidDownArrow />
                    </span>
                  </button>
                </div>
                {/* Dropdown menu */}
                {showDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      minWidth: '245px',
                      marginTop: '2px',
                    }}
                  >
                    <button
                      onClick={handleMultiEdit}
                      style={{
                        width: '100%',
                        padding: '10px 15px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                    >
                      <FaPlus /> Multi Edit
                    </button>
                    <button
                      onClick={handleAssignProcedureGroups}
                      style={{
                        width: '100%',
                        padding: '10px 15px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                    >
                      <FaPlus /> Assign Procedure Groups
                    </button>
                    <button
                      onClick={handlePriceListsGroups}
                      style={{
                        width: '100%',
                        padding: '10px 15px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                    >
                      <TiPencil /> Price Lists Groups
                    </button>
                    <button
                      onClick={handleProcedurePricesEdit}
                      style={{
                        width: '100%',
                        padding: '10px 15px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                    >
                      <TiPencil /> Procedure Prices Edit
                    </button>
                    <button
                      onClick={handleAttributePricesEdit}
                      style={{
                        width: '100%',
                        padding: '10px 15px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                    >
                      <TiPencil /> Attribute Prices Edit
                    </button>
                    <button
                      onClick={handleProcedureQuickCodes}
                      style={{
                        width: '100%',
                        padding: '10px 15px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                    >
                      <FiPrinter /> Procedure Quick Codes
                    </button>
                    <button
                      onClick={handleExportToExcel}
                      style={{
                        width: '100%',
                        padding: '10px 15px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                    >
                      <TbFileDownload /> Export to Excel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f0f0f0', color: 'black' }}>
            <th style={{ padding: '10px', textAlign: 'left' }}>Lab ID</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Name</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Category</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Price</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Sched</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Sort</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.length === 0 ? (
            <tr>
              <td colSpan="7" style={{ padding: '8px', textAlign: 'center' }}>
                {searchTerm ? 'No procedures found matching your search' : 'No procedures found'}
              </td>
            </tr>
          ) : (
            filteredData.map((item) => {
              const defaultPriceObj = item.prices?.find(price => price.target === "default") || item.prices?.[0];
              const displayPrice = defaultPriceObj?.price || item.price || 'N/A';
              return (
                <tr key={item._id} style={{ backgroundColor: '#f9f9f9', borderBottom: '1px solid #ddd' }}>
                  <td style={{ padding: '8px', fontWeight: 'bold' }}>
                    {item.addedBy?.userLabId || 'N/A'}
                  </td>
                  <td style={{ padding: '8px' }}>{item.name}</td>
                  <td style={{ padding: '8px' }}>{item.category}</td>
                  <td style={{ padding: '8px' }}>{displayPrice}</td>
                  <td style={{ padding: '8px' }}>{item.turnAroundDays || 'Sched'}</td>
                  <td style={{ padding: '8px' }}>{item.sortRank || ''}</td>
                  <td style={{ padding: '8px' }}>
                    <button
                      style={{ height: '37px', marginRight: '10px', width: '60px' }}
                      onClick={() => handleEdit(item)}
                      className="btn btn-primary btn-sm"
                    >
                      Edit
                    </button>
                    <button
                      style={{ height: '37px', width: '80px' }}
                      onClick={() => confirmDelete(item._id)}
                      className="btn btn-danger btn-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Show count of procedures */}
      <div style={{ marginTop: '10px', padding: '8px', color: '#666' }}>
        Showing {filteredData.length} of {data.length} procedure(s)
      </div>

      {/* Delete modal */}
      {showDeleteModal && (
        <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#004aad', color: 'white' }}>
                <h5 className="modal-title">Confirm Deletion</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowDeleteModal(false)}
                  style={{ color: 'white', fontSize: '20px' }}
                >
                  <span>×</span>
                </button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this procedure?</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={handleDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default LabProcedureReport;